/**
 * SessionListItem Component
 *
 * Displays a single chat session in the sidebar with actions for rename and delete.
 */

import React, { useState } from 'react';
import { Modal } from 'antd';
import deleteIcon from '../../../../assets/delete.svg';

const SessionListItem = ({
  session,
  isActive = false,
  onSelect,
  onRename,
  onDelete,
  sourceBadge = null
}) => {
  const [isRenaming, setIsRenaming] = useState(false);
  const [editedTitle, setEditedTitle] = useState(session.title || 'Untitled Session');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleDoubleClick = () => {
    setIsRenaming(true);
    setEditedTitle(session.title || 'Untitled Session');
  };

  const handleRenameSubmit = () => {
    const trimmedTitle = editedTitle.trim();
    if (trimmedTitle && trimmedTitle !== session.title && trimmedTitle.length >= 1 && trimmedTitle.length <= 100) {
      onRename(session.id, trimmedTitle);
    } else {
      setEditedTitle(session.title || 'Untitled Session');
    }
    setIsRenaming(false);
  };

  const handleRenameCancel = () => {
    setEditedTitle(session.title || 'Untitled Session');
    setIsRenaming(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleRenameSubmit();
    } else if (e.key === 'Escape') {
      handleRenameCancel();
    }
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    onDelete(session.id);
    setIsDeleteModalOpen(false);
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    return date.toLocaleDateString();
  };

  const messageCount = session.messageCount || 0;

  return (
    <div
      className={`session-list-item ${isActive ? 'active' : ''}`}
      onClick={() => !isRenaming && onSelect(session.id)}
      data-testid={`ai-session-${session.id}`}
      data-active={isActive ? "true" : "false"}
    >
      <div className="session-list-item__content">
        {isRenaming ? (
          <input
            type="text"
            className="session-list-item__title-input"
            value={editedTitle}
            onChange={(e) => setEditedTitle(e.target.value)}
            onBlur={handleRenameSubmit}
            onKeyDown={handleKeyDown}
            maxLength={100}
            autoFocus
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <div
            className="session-list-item__title"
            onDoubleClick={handleDoubleClick}
            title={session.title}
          >
            {session.title || 'Untitled Session'}
          </div>
        )}
        <div className="session-list-item__meta">
          <span className="session-list-item__timestamp">
            {formatTimestamp(session.lastActivity || session.updatedAt || session.createdAt)}
          </span>
          <span className="session-list-item__separator">•</span>
          <span className="session-list-item__message-count">
            {messageCount} message{messageCount !== 1 ? 's' : ''}
          </span>
        </div>
      </div>
      <div className="session-list-item__actions">
        <button
          className="session-list-item__delete-button"
          onClick={handleDeleteClick}
          data-testid="ai-session-delete"
          title="Delete session"
          aria-label="Delete session"
        >
          <img src={deleteIcon} alt="Delete" width="16" height="16" />
        </button>
      </div>

      {/* Delete confirmation modal */}
      <Modal
        title="Delete this session?"
        open={isDeleteModalOpen}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true, "data-testid": "ai-session-delete-confirm" }}
        centered
      >
        <p>This action cannot be undone.</p>
      </Modal>
    </div>
  );
};

export default SessionListItem;
